// models/commerce/OrderBoxItem.js - PRODUCTION READY
import { useAxiosRepo } from '@pinia-orm/axios';
import { Model, useRepo } from 'pinia-orm';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
// ONE categories object. This file used to import a second, stale copy of the
// same name from ItemStatus.js and read isEditable/canCancel off it, so the
// model and the useItemStatus composable disagreed about the same item.
import { ItemStatusCategories } from '@/enums/commerce/ItemStatusConfig.js';
import OrderBox from '@/models/commerce/OrderBox.js';
import Product from '@/models/commerce/Product.js';
import Variant from '@/models/commerce/Variant.js';
import Post from '@/models/posts/Post.js';
import {
  cancelOrderBoxItem,
  requestCancelOrderBoxItem,
  uncancelOrderBoxItem,
} from '@/services/orderBoxItem.service.js';
import { boxCanTakeItBack } from '@/utils/supplierSuggestion.js';

/**
 * Can the member ASK to cancel this line — «اطلب الإلغاء» (BE→FE brief
 * 2026-09-21-member-cancel-request)?
 *
 * The exact complement of `canCancel` on a live plan: where «الغي» is withheld
 * because a collector is out buying the line (`inActivePlan`), this door opens
 * instead — but only while nothing has been bought yet (PENDING, COLLECTING or
 * ISSUE; `ItemStatusCategories.MEMBER_CANCEL_REQUESTABLE`), and only while no
 * request of hers is already waiting (`cancelRequestedAt`). Mirrors
 * `OrderBoxItemPolicy::requestCancel()` + `OrderBoxService::memberRequestCancel()`.
 *
 * Both per-item facts are tested for PRESENCE, as everywhere on this resource:
 * `inActivePlan` is `true` or omitted, `cancelRequestedAt` a time or omitted.
 *
 * A plain function rather than only a getter because the rows and the details
 * sheet hold raw resources in tests and hydrated models in the app; the getter
 * below delegates here so the two cannot drift. The status is coerced for the
 * same reason `useItemStatus` coerces it: a string `"1"` off the wire must not
 * silently answer false.
 *
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function canRequestCancel(item) {
  if (!item) return false;
  if (item.inActivePlan !== true) return false;
  if (hasPendingCancelRequest(item)) return false;
  return ItemStatusCategories.MEMBER_CANCEL_REQUESTABLE.includes(Number(item.status));
}

/**
 * Is her cancel request on this line waiting on the admin? Presence-driven —
 * the key is omitted the moment the admin answers either way.
 *
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function hasPendingCancelRequest(item) {
  return item?.cancelRequestedAt != null && item.cancelRequestedAt !== '';
}

/**
 * OrderBoxItem Model
 *
 * RESPONSIBILITIES:
 * - Schema definition for order items
 * - Status-based business logic
 * - Timeline computation
 * - API operations for items
 */
export default class OrderBoxItem extends Model {
  // ==========================================================================
  // SCHEMA DEFINITION
  // ==========================================================================

  static entity = 'order-box-items';

  static fields() {
    return {
      id: this.uid(),
      avatarUrl: this.attr(''),
      orderId: this.attr(''),
      orderBoxId: this.attr(''),

      // Channel & Product
      channelId: this.attr(''),
      channelName: this.attr(''),
      postId: this.attr(''),
      variantId: this.attr(''),
      variantName: this.attr(''),
      variantAvatarUrl: this.attr(''),
      productId: this.attr(null),
      productName: this.attr(''),
      productAvatarUrl: this.attr(''),

      // Manual (off-catalog) line — admin-added items with no catalog
      // variant/product (e.g. a delivery fee or an ad-hoc line). All the
      // catalog ids/urls above are null for these; the display name lives in
      // manualLabel. `isManual` is the RELIABLE discriminator (sync brief
      // 2026-07-09-order-box-items-manual-item-fields) — never infer "manual"
      // from variantId == null. isCommissionable tells whether the line earns
      // PoolShare (manual lines may be false). Defaults mirror a catalog line.
      isManual: this.attr(false),
      manualLabel: this.attr(null),
      isCommissionable: this.attr(true),

      // A «تدبيسة» line: a piece from the club's own resale shelf, final sale
      // (BE→FE brief 2026-10-12-tadbeesa-owner-payout, `OrderBoxItem::isResale()`).
      // A plain bool on every line since that brief — and the RELIABLE answer to
      // "is this her live تدبيسة line?", which the size sheet used to infer from
      // the post. Null is not a wire value: it is what a payload from a backend
      // older than the field leaves here, and it means "not told", never "no".
      isResale: this.attr(null),

      // Status & Pricing
      status: this.attr(ItemStatus.PENDING),
      price: this.attr(0),        // gross unit price (retail) — kept for audit
      discount: this.attr(0),     // per-line discount cents (Poola: PoolShare-funded share)
      amount: this.attr(null),    // net line total (qty*price − discount) = cash paid; null on legacy payloads
      qty: this.attr(0),
      notes: this.attr(''),

      // Reservation
      reservationStatus: this.attr('none'),
      depositAmount: this.attr(0),
      reservedForName: this.attr(''),

      // Member-answerable collection issue — the compact "needs your decision"
      // signal surfaced by the backend OrderBoxItemResource, gated by the
      // `panels.collection.member_issues_enabled` flag (brief
      // 2026-07-21-member-answerable-collection-issues). Shape (or null):
      //   { id, reason, status, awaitingMember:bool, allowedResponses:string[] }
      // When the flag is ON every serialization emits this key (null when the
      // item has no live issue), so it's always fresh — no merge staleness. The
      // full member-visible detail lives on the ItemIssue resource
      // (GET /item-issues/{id}); this only drives the badge + the decisions inbox.
      activeIssue: this.attr(null),

      // The item's most-recent issue in ANY state (brief
      // 2026-07-26-member-issue-fields-asks-2-3-7d-8, ASK 8). Emitted next to
      // `activeIssue` under the same feature flag, but PERMANENT: `activeIssue`
      // is a HasOne of the NON-resolved issue and goes null the moment the
      // leader resolves it, while this keeps pointing at the row. It is what
      // lets a resolved / out-of-stock item still open its explanation +
      // timeline instead of dead-ending. Null when the item never had one.
      lastIssueId: this.attr(null),

      // "Last marked out of stock", NARROWED TO THIS LINE (BE→FE brief
      // 2026-08-15). Same shape and the same must-be-declared-or-it-vanishes
      // rule as Post.js — see the long note there.
      //
      // The difference from a post or a product is the one that matters to a
      // member: a post card says "something here is out"; this line reports
      // ONLY the mark covering its OWN variant, plus a whole-product claim. A
      // sibling variant being out never badges her line. That narrowing is done
      // backend-side, so nothing here has to re-derive it.
      //
      // There is no `lastCollected` on this resource, deliberately: what the
      // club last paid for a product is a browsing fact, and a line she has
      // already ordered carries its own agreed price.
      availability: this.attr(null),

      // The shop SHE named for a line nobody could source, and ONLY while the
      // admin still owes her an answer about it (BE→FE brief
      // 2026-09-05-member-supplier-suggestion). Shape, when present:
      //   { at: ISO, supplierName: string, note: string|null }
      //
      // Same omitted-key discipline as `availability` above: the backend never
      // emits it as null, and the key DISAPPEARS the moment the admin accepts or
      // declines — that disappearance is the signal to stop showing "waiting on
      // the club" and to let her suggest again. Read it through
      // utils/supplierSuggestion.js, never off the raw attribute.
      //
      // A second POST while one is open UPDATES it — there is never a second row.
      supplierSuggestion: this.attr(null),

      // WHY a terminal line ended the way it did (BE→FE brief
      // 2026-09-05-declined-resolution-and-loss-reason). Additive and omitted
      // when the line carries no reason.
      //
      // The value that changes what she reads is `not_as_ordered`: the shop HAD
      // the product, it just was not her version — so the chip must say neither
      // «ملغي» (that reads as *she* cancelled) nor «نواقص» (a false claim about
      // the product). Nothing was bought and nothing was charged, in either
      // direction, so nothing here may promise money back. The label rule lives
      // beside the CANCELLED meta in enums/commerce/ItemStatusConfig.js.
      //
      // The other values are post-collection write-offs (lost_in_transit,
      // damaged_on_arrival, returned_to_supplier, …) and are not new.
      lossReason: this.attr(null),

      // "A collector is out buying this RIGHT NOW" (BE→FE brief
      // 2026-09-07-member-contract-round-two). `true` or OMITTED — never
      // `false`, so an absent key and `false` mean the same thing to the only
      // question this answers: may the app OFFER a door on this line, or would
      // the policy refuse it?
      //
      // TWO doors read it today — the suggest-a-shop sheet
      // (utils/supplierSuggestion.js) and Cancel (useItemStatus + the getter
      // below). EDIT does not: VariantItemsEditor still learns about the lock
      // from the 403 and handles it post-hoc. That is a coverage gap, not a
      // contract difference — the same fact would answer it.
      //
      // Its predicate is exactly the write locks' own — a try row that has not
      // released the line, on a CONFIRMED or IN_PROGRESS plan — so what the app
      // hides is precisely what the backend would refuse. Narrower than "the
      // line is on a plan": a try row that skipped, cancelled or marked the line
      // out of stock has RELEASED it and does not count, and neither does a
      // draft plan nobody confirmed nor a completed one.
      //
      // This closes the gap the MEMBER_CANCELLABLE docblock in
      // enums/commerce/ItemStatusConfig.js has described since August: the PWA
      // could not model the in-active-plan 403 and offered the control until the
      // first refusal.
      inActivePlan: this.attr(null),

      // "She asked to cancel this line, and the admin has not answered yet"
      // (BE→FE brief 2026-09-21-member-cancel-request). An ISO-8601 time, or
      // OMITTED — present only while her request waits, the same "present or
      // omitted" rule as `inActivePlan` above. It disappears on either answer:
      // an approve arrives as a CANCELLED line, a decline as the line unchanged.
      //
      // Read by `canRequestCancel()` (the door stays shut while it is set) and
      // by the waiting chip that takes the door's place on the row and in the
      // details sheet.
      cancelRequestedAt: this.attr(null),

      // "She has already named a shop for this line, and it was answered" —
      // the once-per-line gate (BE→FE brief
      // 2026-09-15-roster-only-tips-once-per-line, owner answer H-B).
      //
      // `true` or OMITTED, never `false`, so the only honest test is
      // `=== true`. It is read by `canSuggestSupplier()` and by nothing else:
      // the DOOR asks this, while the RECEIPT on screen reads
      // `supplierSuggestion`. Keeping the two apart is deliberate — an answered
      // tip drops off the payload after thirty days and this flag does not, so
      // deriving one from the other would re-open a permanently closed door a
      // month later.
      //
      // An OPEN tip does not set it. Correcting a tip the club has not answered
      // yet is still hers to do, and a re-POST updates the same row.
      supplierSuggestionUsed: this.attr(null),

      // Her post-delivery return on this line, or one the leader's own
      // «مش زي ما طلب» door opened against it (BE→FE briefs 2026-09-17:
      // `member-return-request.md`, `returns-desk-door-and-box.md`). Same
      // omitted-key discipline as `supplierSuggestion` above: OMITTED (never
      // null) while the line carries no return, and hidden again once a
      // return on it is CANCELLED. Shape, when present:
      //   { status: MemberReturnStatus, reasonKey: SupplierReturnReason }
      // Read it through utils/supplierReturn.js, never off the raw attribute.
      supplierReturn: this.attr(null),

      // Relationships
      orderBox: this.belongsTo(OrderBox, 'orderBoxId'),
      product: this.belongsTo(Product, 'productId'),
      variant: this.belongsTo(Variant, 'variantId'),
      post:this.belongsTo(Post, 'postId'),
      createdAt: this.attr(''),
      updatedAt: this.attr(''),

      // Timeline dates
      orderedAt: this.attr(null),
      collectingStartedAt: this.attr(null),
      collectedAt: this.attr(null),
      packagedAt: this.attr(null),
      shippedAt: this.attr(null),
      deliveredAt: this.attr(null),
      cancelledAt: this.attr(null),
      issueDetectedAt: this.attr(null),
    };
  }

  // ==========================================================================
  // THE OMITTED-KEY CONTRACT
  // ==========================================================================

  /**
   * The attributes the backend OMITS rather than nulls. Every one of them means
   * "this fact is not true of the line right now", and their ABSENCE from a
   * payload is the only way the wire can say so.
   */
  static OMITTED_WHEN_ABSENT = [
    'supplierSuggestion',
    // "This line has spent its one tip." `true` or omitted, never `false`
    // (oos-hold P1-B, brief 2026-09-15-roster-only-tips-once-per-line, owner
    // answer H-B). It belongs here for the usual reason and for a sharper one
    // than `inActivePlan`'s: unlike `supplierSuggestion`, which AGES OFF after
    // thirty days, this flag never does — so a stale `true` surviving a payload
    // that no longer carries it would keep the door shut on a line the backend
    // has re-opened. It is the door's only gate, and a wrong one is invisible.
    'supplierSuggestionUsed',
    'availability',
    'lossReason',
    // `true` or omitted, never `false` — so it belongs here for the same reason
    // as the other three, and for a sharper one: a stale `true` surviving after
    // the collector released the line would keep every door SHUT on a line the
    // backend would now happily accept.
    'inActivePlan',
    // Present only while her cancel request waits. A stale value surviving a
    // DECLINE — which arrives as the line unchanged, minus this key — would
    // keep «مستني رد» on screen and the request door shut until a reload.
    'cancelRequestedAt',
  ];

  /**
   * Turn an omitted key back into `null` on an UPDATE.
   *
   * WHY THIS EXISTS (defect U-30). pinia-orm merges an update as
   * `Object.assign(hydrate(existing), record)` (pinia-orm/dist/index.mjs
   * saveElements). That keeps every key the new payload does not mention — which
   * is exactly right for a partial save and exactly wrong for a full resource off
   * the wire, because the backend's contract is that these facts DISAPPEAR when
   * they stop being true. The member's own receipt was the visible cost: the
   * admin accepts or declines her supplier suggestion, the backend drops
   * `supplierSuggestion`, and her device kept saying «اقتراحك وصل للفريق» — and
   * kept the door shut — until she reloaded the app.
   *
   * WHY IT LIVES ON THE MODEL and not at the call sites. Twelve entry points save
   * this resource: six explicit `useRepo(OrderBoxItem).save()` calls and six
   * `save: true` axios reads across OrderBoxRepository, plus the model's own
   * cancel/uncancel. A normalisation at any one of them is a normalisation the
   * next one forgets. This hook is the ONE place absence becomes null, and it
   * sits on the object every one of those paths goes through.
   *
   * HOW A PARTIAL SAVE IS RECOGNISED. `status` is present on every full wire
   * resource and on none of the partial writes: `useItemIssue.js`'s
   * `repo.save({ id, activeIssue })`, which RELIES on the merge to leave the rest
   * of the line alone, and the bare `{id, type}` stubs jsona emits for any
   * relationship a payload references without including it. So a record with no
   * `status` key is left untouched; anything else has its omitted facts nulled.
   *
   * ⚠ IT IS AN OPT-OUT HEURISTIC GUARDING A DESTRUCTIVE WRITE, and it has one
   * failure mode worth knowing before you add a read. A SPARSE FIELDSET that
   * happens to name `status` — `fields[order-box-items]=status,qty` — looks
   * exactly like a full resource here and would null a live `supplierSuggestion`
   * with no error anywhere. No such read exists today, but the backend declared
   * `lossReason` and `inActivePlan` for sparse fieldsets on 2026-09-07 and
   * suggested using them, so the first one is plausible. If a sparse read on
   * this resource is ever added, invert this to opt-in — a `saveWireResource()`
   * helper used by the authoritative savers (OrderBoxRepository's six and this
   * model's two) — rather than widening the heuristic.
   *
   * Mutating `model` is the documented way to influence the write: the hook runs
   * with the model live, and `saveElements` persists `model.$getAttributes()`
   * afterwards. Returning `false` would ABORT the save, so this never returns.
   */
  static updating(model, record) {
    if (!record || typeof record !== 'object') return;
    if (!('status' in record)) return;

    for (const key of OrderBoxItem.OMITTED_WHEN_ABSENT) {
      if (!(key in record)) model[key] = null;
    }
  }

  // ==========================================================================
  // BUSINESS LOGIC - Status Checks
  // ==========================================================================

  get isEditable() {
    return ItemStatusCategories.EDITABLE.includes(this.status);
  }

  get isCancelled() {
    return this.status === ItemStatus.CANCELLED;
  }

  // "The member can still act on this line" — NOT "blocks shipping". The old
  // change-supplier / wait-for-restock / cancel resolution sheet this used to
  // drive no longer exists; what it gates now is the member-answerable issue
  // surface (the decisions inbox entry and the issue detail/timeline route).
  // OUT_OF_STOCK belongs here — a member may still open it to read what
  // happened — but must never gate shipping; ship-blocking is BLOCKS_SHIPPING
  // (= ISSUE only).
  get isIssue() {
    return ItemStatusCategories.RESOLUTION_ACTIONABLE.includes(this.status);
  }

  get isInProgress() {
    return [ItemStatus.PENDING, ItemStatus.COLLECTING].includes(this.status);
  }

  get isDone() {
    return [ItemStatus.COLLECTED, ItemStatus.PACKAGED].includes(this.status);
  }

  /**
     * The repoint from the stale categories object to the live one is
     * BEHAVIOUR-NEUTRAL here: stale EDITABLE was [PENDING] and live EDITABLE is
     * [PENDING, CANCELLED], and `&& !isCancelled` collapses the two to the same
     * answer — PENDING only. Pinned by tests/unit/models/OrderBoxItem.memberGates.test.js.
     */
  get canEdit() {
    return this.isEditable && !this.isCancelled;
  }

  /**
     * Can the MEMBER cancel this line? Mirrors OrderBoxService::memberCancelItem's
     * source-status gate — PENDING only.
     *
     * This used to be `!isCancelled && !TERMINAL`, and TERMINAL was then just
     * [DELIVERED, OUT_OF_STOCK] — so it said yes on COLLECTING, ISSUE,
     * AWAITING_APPROVAL, COLLECTED and PACKAGED. For a line NOT in an active
     * plan the backend answers those with a 422 whose body carries the English
     * exception sentence, and VariantItemsEditor prefers `error.detail` over its
     * own Arabic copy — so an Arabic member was answered in English. That is the
     * path this narrowing closes.
     *
     * The other refusal — a PENDING line already inside a CONFIRMED/IN_PROGRESS
     * collection plan — is a 403 from OrderBoxItemPolicy::cancel, and the PWA
     * already handles that one in Arabic (`item.status.errors.planLocked`, plus
     * `planLockedItemIds` disabling the control afterwards). It could not be
     * modelled here until 2026-09-07, because `isInActivePlan()` had no field on
     * OrderBoxItemResource; `inActivePlan` is that field, so both halves of the
     * refusal are now predictable and the control is withheld up front.
     *
     * Gated here AND in `useItemStatus.canCancel` deliberately. This getter has
     * no production consumer today, but the two have disagreed about one item
     * before (see the note at the top of this file about the duplicated
     * categories object), and a gate present in one and absent in the other is
     * exactly how that happens again.
     */
  get canCancel() {
    if (!ItemStatusCategories.MEMBER_CANCELLABLE.includes(this.status)) return false;
    if (this.inActivePlan === true) return false;

    // The carton gate (oos-hold P1-A, owner answer H-M). An OUT_OF_STOCK line
    // is cancellable only while its carton can still take the item back, which
    // is exactly the window in which her money is still held for it.
    //
    // This getter reads the relation it already has; `useItemStatus.canCancel`
    // takes the carton as an argument because its callers hold a raw resource
    // rather than a hydrated model. Same rule, one definition of it
    // (`boxCanTakeItBack`), two ways of reaching the carton — which is the
    // shape this getter's own docblock already argues for.
    if (this.status === ItemStatus.OUT_OF_STOCK) {
      return boxCanTakeItBack(this.orderBox);
    }

    return true;
  }

  /**
     * Can the member ASK to cancel this line? See the exported
     * `canRequestCancel()` below — one definition, reachable from a hydrated
     * model and from the raw resources the components hold.
     */
  get canRequestCancel() {
    return canRequestCancel(this);
  }

  // ==========================================================================
  // PRICING CALCULATIONS
  // ==========================================================================

  get totalPrice() {
    // Net line total: the backend's generated `amount` (qty*price − discount) is
    // the cash paid — for a Poola line this excludes the PoolShare-funded share.
    // Falls back to gross price*qty for legacy payloads without `amount`.
    return typeof this.amount === 'number' ? this.amount : this.price * this.qty;
  }

  get formattedPrice() {
    return `${(this.price / 100).toFixed(2)} EGP`;
  }

  get formattedTotal() {
    return `${(this.totalPrice / 100).toFixed(2)} EGP`;
  }

  // ==========================================================================
  // TIMELINE - Computed from date fields
  // ==========================================================================

  get timeline() {
    const events = [
      { status: ItemStatus.PENDING, date: this.orderedAt, key: 'ordered' },
      { status: ItemStatus.COLLECTING, date: this.collectingStartedAt, key: 'collecting' },
      { status: ItemStatus.COLLECTED, date: this.collectedAt, key: 'collected' },
      { status: ItemStatus.PACKAGED, date: this.packagedAt, key: 'packaged' },
      { status: ItemStatus.SHIPPED, date: this.shippedAt, key: 'shipped' },
      { status: ItemStatus.DELIVERED, date: this.deliveredAt, key: 'delivered' },
    ];

    if (this.cancelledAt) {
      events.push({ status: ItemStatus.CANCELLED, date: this.cancelledAt, key: 'cancelled' });
    }
    if (this.issueDetectedAt) {
      events.push({ status: this.status, date: this.issueDetectedAt, key: 'issue' });
    }

    return events
      .filter(e => e.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  get lastUpdatedDate() {
    if (this.timeline.length === 0) return null;
    return this.timeline[this.timeline.length - 1].date;
  }

  // ==========================================================================
  // RESERVATION
  // ==========================================================================

  get isReserved() {
    return this.reservationStatus && this.reservationStatus !== 'none';
  }

  get reservationDisplayName() {
    return this.isReserved ? (this.reservedForName || 'غير محدد') : '';
  }

  // ==========================================================================
  // API METHODS
  // ==========================================================================

  /**
     * Update item attributes (qty / notes / reservation).
     * Cancellation goes through the dedicated /cancel JSON:API action — see
     * cancel() below — so status updates are stripped here.
     *
     * @param {Object} updates
     * @returns {Promise<OrderBoxItem>}
     */
  async update(updates) {
    const cleanUpdates = { ...updates };
    delete cleanUpdates.status;

    await useAxiosRepo(OrderBoxItem).api().patch(
      `/order-box-items/${this.id}`,
      {
        data: {
          type: 'order-box-items',
          id: this.id,
          attributes: cleanUpdates,
        },
      },
    );

    // Refresh parent box to update totals
    if (this.orderBoxId) {
      await OrderBox.fetchOne(this.orderBoxId);
    }

    return OrderBoxItem.find(this.id);
  }

  /**
     * Cancel this item via the dedicated POST /order-box-items/{id}/cancel
     * JSON:API action. Errors carry a `code` of ORDER_BOX_ITEM_PLAN_LOCKED
     * (403) or ORDER_BOX_ITEM_STALE_STATE (422).
     *
     * @param {string} [reason] free-text audit note (<= 500 chars)
     * @returns {Promise<OrderBoxItem>}
     */
  async cancel(reason) {
    const updated = await cancelOrderBoxItem(this.id, reason);
    useRepo(OrderBoxItem).save(updated);
    if (this.orderBoxId) {
      await OrderBox.fetchOne(this.orderBoxId);
    }
    return updated;
  }

  /**
     * Ask the admin to cancel this line — the door for a line a collector is
     * already out buying, where `cancel()` above is refused. Puts the returned
     * line (now carrying `cancelRequestedAt`) back in the store.
     */
  async requestCancel(reason) {
    const updated = await requestCancelOrderBoxItem(this.id, reason);
    useRepo(OrderBoxItem).save(updated);
    return updated;
  }

  /**
     * Undo a prior cancellation.
     */
  async uncancel(reason) {
    const updated = await uncancelOrderBoxItem(this.id, reason);
    useRepo(OrderBoxItem).save(updated);
    if (this.orderBoxId) {
      await OrderBox.fetchOne(this.orderBoxId);
    }
    return updated;
  }

  /**
     * Delete item from box
     */
  async destroy() {
    await useAxiosRepo(OrderBoxItem).api().delete(`/order-box-items/${this.id}`);

    // Refresh parent box to update totals
    if (this.orderBoxId) {
      await OrderBox.fetchOne(this.orderBoxId);
    }

    // Manual deletion
    this.$delete();
  }

  // ==========================================================================
  // STATIC METHODS - Batch Operations
  // ==========================================================================

  /**
     * Add multiple items to a box
     *
     * @param {string} boxId
     * @param {string} productId
     * @param {Array} items - [{ variantId, qty, notes }]
     * @returns {Promise<OrderBoxItem[]>}
     */
  static async addToBox(boxId, productId, items) {
    await useAxiosRepo(this).api().post(
      `/order-boxes/${boxId}/product-items/${productId}`,
      { data: items },
    );

    // Refresh parent box
    await OrderBox.fetchOne(boxId);

    return this.query().where('orderBoxId', boxId).get();
  }

  /**
     * Fetch items for a specific post
     *
     * @param {number} postId
     * @returns {Promise<OrderBoxItem[]>}
     */
  static async fetchForPost(postId) {
    await useAxiosRepo(this).api().get(
      `/posts/${postId}/order-box-items`,
      { save: true },
    );

    return this.query()
      .where('postId', parseInt(postId))
      .with('product')  // Explicit, not withAllRecursive
      .get();
  }
}
